import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  useTheme,
  alpha,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Person, 
  TrendingUp, 
  SportsSoccer, 
  EmojiEvents,
  Schedule,
  Star,
  Timeline,
  TrendingDown,
  Edit,
  PhotoCamera,
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  GpsFixed,
  Upload,
  CloudUpload 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiConfig } from '../config/api';

const Profile = () => {
  const { user } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    avatarUrl: '',
    showEmail: false
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const theme = useTheme();

  const isOwnProfile = !userId || userId === user?.id;

  useEffect(() => {
    fetchProfileData();
  }, [userId, user]);

  const fetchProfileData = async () => {
    try {
      const targetUserId = userId || user?.id;
      if (!targetUserId) return;

      const [profileResponse, statsResponse] = await Promise.all([
        axios.get(apiConfig.users.profile(targetUserId)),
        axios.get(apiConfig.users.stats(targetUserId))
      ]);
      
      setProfileUser(profileResponse.data);
      setStats(statsResponse.data);
      
      if (isOwnProfile) {
        setEditForm({
          name: profileResponse.data.name || '',
          bio: profileResponse.data.bio || '',
          avatarUrl: profileResponse.data.avatarUrl || '',
          showEmail: profileResponse.data.showEmail || false
        });
      }
    } catch (error) {
      setError('Käyttäjätilastojen lataus epäonnistui');
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = () => {
    setEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      const response = await axios.put(
        apiConfig.users.updateProfile,
        editForm
      );
      setProfileUser(response.data);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Profiilin päivitys epäonnistui');
    }
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear preview image when URL changes
    if (field === 'avatarUrl') {
      setPreviewImage(null);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setError('Kuvatiedosto on liian suuri. Maksimikoko on 10MB.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Valitse kuvatiedosto (JPG, PNG, GIF, etc.)');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post(
        apiConfig.users.uploadAvatar,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update form with new avatar URL
      handleFormChange('avatarUrl', response.data.avatarUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Kuvan lataus epäonnistui. Yritä uudelleen.');
      setPreviewImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fi-FI');
  };

  const getResultColor = (points) => {
    if (points === 3) return 'success';
    if (points === 1) return 'warning';
    return 'error';
  };

  const getResultText = (points) => {
    if (points === 3) return 'Tarkka tulos';
    if (points === 1) return 'Oikea voittaja';
    return 'Väärin';
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Ladataan profiilitietoja...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        {/* Profile Header */}
        <Card 
          sx={{ 
            mb: 6, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={4}>
              <Avatar 
                src={profileUser?.avatarUrl} 
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: `4px solid ${theme.palette.primary.main}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                }}
              >
                <Person sx={{ fontSize: 60 }} />
              </Avatar>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      fontWeight: 700,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {profileUser?.name || 'Käyttäjä'}
                  </Typography>
                  {isOwnProfile && (
                    <Tooltip title="Muokkaa profiilia">
                      <IconButton 
                        onClick={handleEditProfile}
                        sx={{ 
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                {(profileUser?.showEmail || isOwnProfile) && (
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {profileUser?.email}
                    {isOwnProfile && !profileUser?.showEmail && (
                      <Chip 
                        label="Piilotettu" 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                      />
                    )}
                  </Typography>
                )}
                
                {profileUser?.bio && (
                  <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mt: 1, fontStyle: 'italic' }}>
                    {profileUser.bio}
                  </Typography>
                )}
                
                {stats && (
                  <Box display="flex" gap={2} mt={2}>
                    <Chip 
                      icon={<Star />}
                      label={`${stats.totalPoints} pistettä`}
                      color="primary"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip 
                      icon={<GpsFixed />}
                      label={`${stats.accuracy}% tarkkuus`}
                      color="secondary"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4, 
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '1rem',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {stats && (
          <Grid container spacing={4}>
            {/* Statistics Cards */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={3} mb={4}>
                <Grid item xs={6} sm={3}>
                  <Card 
                    sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderRadius: 3,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Star sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                        {stats.totalPoints}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Pistettä Yhteensä
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card 
                    sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                      borderRadius: 3,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Timeline sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.5 }}>
                        {stats.totalPredictions}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Ennustusta
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card 
                    sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      borderRadius: 3,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
                        {stats.accuracy}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Tarkkuus
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card 
                    sx={{ 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      borderRadius: 3,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <TrendingUp sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 0.5 }}>
                        {stats.avgPointsPerGame.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Keskim./Ottelu
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Detailed Stats */}
              <Card 
                sx={{ 
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    <TrendingUp sx={{ mr: 1.5, verticalAlign: 'middle' }} />
                    Yksityiskohtaiset Tilastot
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box mb={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Onnistuneet ennustukset:
                          </Typography>
                          <Chip 
                            label={`${stats.correctPredictions}/${stats.totalPredictions}`}
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(stats.correctPredictions / stats.totalPredictions) * 100} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.success.main, 0.2),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Tarkat tulosennustukset:
                        </Typography>
                        <Chip 
                          label={stats.exactPredictions}
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Games */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  height: 'fit-content',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    <Schedule sx={{ mr: 1.5, verticalAlign: 'middle' }} />
                    Viimeisimmät Ottelut
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {stats.recentGames && stats.recentGames.length > 0 ? (
                    <List dense sx={{ py: 0 }}>
                      {stats.recentGames.map((game, index) => (
                        <ListItem 
                          key={index} 
                          sx={{ 
                            px: 0,
                            py: 2,
                            borderBottom: index < stats.recentGames.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {game.homeTeam} vs {game.awayTeam}
                                </Typography>
                                <Chip 
                                  label={`${game.pointsEarned} p`}
                                  color={getResultColor(game.pointsEarned)}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  Ennuste: {game.predictedHomeScore}-{game.predictedAwayScore} | 
                                  Tulos: {game.homeScore}-{game.awayScore}
                                </Typography>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(game.gameDate)}
                                  </Typography>
                                  <Chip 
                                    label={getResultText(game.pointsEarned)}
                                    color={getResultColor(game.pointsEarned)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box textAlign="center" py={4}>
                      <Schedule sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ei viimeaikaisia otteluita
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tee ennustuksia nähdäksesi tulokset täällä!
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
      
      {/* Edit Profile Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Edit />
            Muokkaa Profiilia
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nimi"
                value={editForm.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box>
                <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                  Profiilikuva
                </Typography>
                
                {/* Upload Button */}
                <Box display="flex" gap={2} mb={2}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={uploadingImage ? <CircularProgress size={20} /> : <CloudUpload />}
                    disabled={uploadingImage}
                    sx={{ minWidth: 160 }}
                  >
                    {uploadingImage ? 'Ladataan...' : 'Lataa kuva'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    tai syötä URL alla
                  </Typography>
                </Box>
                
                {/* URL Input */}
                <TextField
                  fullWidth
                  label="Profiilikuvan URL"
                  value={editForm.avatarUrl}
                  onChange={(e) => handleFormChange('avatarUrl', e.target.value)}
                  variant="outlined"
                  placeholder="https://example.com/avatar.jpg"
                  helperText="Maksimikoko: 10MB. Tuetut tiedostotyypit: JPG, PNG, GIF"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Biografia"
                value={editForm.bio}
                onChange={(e) => handleFormChange('bio', e.target.value)}
                variant="outlined"
                placeholder="Kerro itsestäsi..."
                helperText="Maksimissaan 200 merkkiä"
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.showEmail}
                    onChange={(e) => handleFormChange('showEmail', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {editForm.showEmail ? <Visibility /> : <VisibilityOff />}
                    Näytä sähköpostiosoite muille käyttäjille
                  </Box>
                }
              />
            </Grid>
            
            {(editForm.avatarUrl || previewImage) && (
              <Grid item xs={12}>
                <Box textAlign="center">
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Profiilikuvan esikatselu:
                  </Typography>
                  <Avatar 
                    src={previewImage || editForm.avatarUrl} 
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      mx: 'auto',
                      border: `3px solid ${theme.palette.primary.main}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }}
                  >
                    <Person sx={{ fontSize: 40 }} />
                  </Avatar>
                  {previewImage && (
                    <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 1 }}>
                      Uusi kuva ladattu! Muista tallentaa muutokset.
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            startIcon={<Cancel />}
            color="inherit"
          >
            Peruuta
          </Button>
          <Button 
            onClick={handleSaveProfile}
            variant="contained"
            startIcon={<Save />}
            color="primary"
          >
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
